import axios from "axios";
import type { JobReference } from "../Services/JobRefreshCoordinator";
import { buildOrchestratorHeaders } from "./orchestratorClientScope";

export interface SmartleadActionRef { account_id: string; action_id: string }
export interface SmartleadSenderMetadata {
  sender_mode?: "fixed" | "smartlead_rotation";
  confirmed_sender?: string | null;
  confirmed_sender_id?: number | null;
  eligible_mailboxes?: { id: number; email: string; name: string }[];
}
export interface SmartleadAvailability extends SmartleadSenderMetadata {
  available: boolean;
  sender?: string;
  sender_name?: string;
  signature?: string;
}
export interface SmartleadSavedDraft { id: number; subject?: string | null; message: string }
export interface SmartleadSubmission extends SmartleadSenderMetadata {
  change_version?: number;
  requester_id?: string | null;
  job_reference?: JobReference | null;
  id: string;
  status: "pending" | "dispatching" | "queued" | "sent" | "failed" | "uncertain" | "suppressed";
  subject: string;
  message: string;
  recipient: string;
  sender: string;
  approved_at: string;
  sent_at: string | null;
  reason: string | null;
  outcome: string | null;
  task_completed_at?: string | null;
  task_completion_requested?: boolean;
  reply_kind?: "human" | "automatic" | "unknown" | null;
  outcome_checked_at?: string | null;
}
export interface SmartleadDeliverySummary extends SmartleadSenderMetadata {
  submission_id: string;
  status: SmartleadSubmission["status"];
  reason: string | null;
  outcome: string | null;
  reply_kind?: SmartleadSubmission["reply_kind"];
  sender: string;
  queued_at: string;
  sent_at: string | null;
}

export function smartleadSenderLabel(sender: SmartleadSenderMetadata & { sender?: string }): string {
  if (sender.confirmed_sender) return `From ${sender.confirmed_sender}`;
  if (sender.sender_mode === "smartlead_rotation") return "Sender assigned by Smartlead";
  return sender.sender ? `From ${sender.sender}` : "Sender unavailable";
}

export function completedDeliveryLabel(delivery: Pick<SmartleadDeliverySummary, "status" | "outcome">): string {
  if (["failed", "uncertain", "suppressed"].includes(delivery.status) || ["bounced", "blocked"].includes(delivery.outcome ?? "")) return "Done · Sending needs attention";
  return delivery.status === "sent" ? "Done · Sent" : "Done · Waiting to send";
}

export function deliveryReasonLabel(reason: string): string {
  if (reason.startsWith("context_changed:")) return `Sending stopped. ${reason.slice("context_changed:".length).trim()}. The action remains done.`;
  return {
    campaign_has_unverified_leads: "Campaign restart is waiting for an unknown lead or unconfirmed send to be verified. Campaign checks retry every 10 minutes.",
    campaign_not_active: "Campaign is paused or inactive. Campaign checks retry every 10 minutes.",
    sender_signature_changed: "The mailbox signature changed. Review it against the approved signature.",
    sender_configuration_changed: "Mailbox SMTP, IMAP, or sender identity needs review.",
    provider_block_list: "Smartlead blocked this address. Further outreach stopped.",
    verify_provider_history_before_retry: "Smartlead may have accepted this email. Sending stays blocked until provider history confirms the handoff.",
    provider_response_unknown: "The provider handoff is uncertain. Verify Smartlead history before another send.",
    provider_sender_or_copy_unverified: "Smartlead history could not verify the sender and approved copy. Sending stays blocked until reconciled.",
    campaign_original_mailbox_unavailable: "The conversation's original mailbox is unavailable. Follow-ups keep that sender and wait.",
    campaign_mailbox_warmup_unverified: "An attached mailbox needs active warmup of at least 14 days and reputation of at least 80%.",
    campaign_mailbox_unavailable: "An attached mailbox is suspended or its SMTP/IMAP connection needs attention.",
    campaign_mailbox_capacity_unverified: "Smartlead mailbox limits or usage could not be verified.",
    campaign_mailbox_capacity_reserved: "The original mailbox is full or has unresolved sends reserving capacity.",
    campaign_mailbox_capacity_full: "All attached mailboxes have reached their configured capacity.",
    campaign_mailbox_spacing: "Waiting for the original mailbox's configured sending interval.",
    campaign_daily_capacity_reserved: "Shared mailbox usage may have reached the campaign's daily limit. Waiting for verified capacity.",
    campaign_mailbox_pool_changed: "Mailbox attachments changed. Rechecking before sending.",
    campaign_reply_throttling_unverified: "Native reply throttling needs verification before follow-ups can send.",
    campaign_rotation_conversion_required: "This existing approval needs a verified conversion before using rotation.",
    approved_context_changed: "The approved draft or action context changed. Review the existing approval.",
  }[reason] ?? reason.replaceAll("_", " ");
}
export interface SmartleadQueueItem extends SmartleadSubmission {
  account_id: string;
  account_name: string;
  action_id: string;
  user_id: string;
  needs_attention: boolean;
  explanation: string;
  smartlead_url: string;
}
export interface SmartleadQueuePage {
  available: boolean;
  items: SmartleadQueueItem[];
  total: number;
  offset: number;
  limit: number;
}
export interface SmartleadQueueFilters {
  account_ids?: string[];
  filter_user_ids?: string[];
  filter_whitelist_ids?: string[];
  filter_sales_leader_ids?: string[];
  filter_sales_leader_whitelist_ids?: string[];
  filter_unassigned_sales_leader?: boolean;
  campaign_id?: string;
  all_campaigns?: boolean;
}
export const SMARTLEAD_QUEUE_CHANGED = "intentico:smartlead-queue-changed";
export interface SmartleadReview extends SmartleadSenderMetadata {
  action: SmartleadActionRef;
  review_token: string;
  recipient: string;
  sender: string;
  sender_name: string;
  subject: string;
  message: string;
  signature: string;
  is_followup: boolean;
  previous_message: string | null;
  sending_window: string;
}
export interface SmartleadReviewResult {
  review: SmartleadReview | null;
  submission: SmartleadSubmission | null;
}

async function request<T>(clientId: string | null, path: string, data?: unknown, params?: object): Promise<T> {
  const response = await axios.request<T>({
    url: `${import.meta.env.VITE_ORCHESTRATOR_BASE_URL}/v1/smartlead/${path}`,
    method: data === undefined ? "GET" : "POST",
    headers: buildOrchestratorHeaders(clientId), data, params,
  });
  return response.data;
}
export const smartleadAvailability = (clientId: string | null, action?: SmartleadActionRef) => request<SmartleadAvailability>(clientId, "availability", undefined, action);
export const queueSmartleadEmail = (clientId: string | null, action: SmartleadActionRef, draft: SmartleadSavedDraft) =>
  request<SmartleadSubmission>(clientId, "queue", { ...action, prompt_id: draft.id, subject: draft.subject ?? null, message: draft.message });
export const prepareSmartleadReview = (clientId: string | null, action: SmartleadActionRef) => request<SmartleadReviewResult>(clientId, "review", action);
export const getSmartleadSubmission = (clientId: string | null, action: SmartleadActionRef) => request<SmartleadReviewResult>(clientId, "submission", undefined, action);
export const approveSmartleadReview = (clientId: string | null, review: SmartleadReview, subject: string, message: string) =>
  request<SmartleadSubmission>(clientId, "approve", { ...review.action, review_token: review.review_token, subject, message });

export function getSmartleadSubmissions(clientId: string, filters: SmartleadQueueFilters, offset = 0) {
  const params = new URLSearchParams({ offset: String(offset), limit: "20" });
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) value.forEach(item => params.append(key, item));
    else if (value !== undefined) params.set(key, String(value));
  }
  return request<SmartleadQueuePage>(clientId, "submissions", undefined, params);
}

export function smartleadStatusLabel(status: SmartleadSubmission["status"]): string {
  return { pending: "Waiting to send", dispatching: "Checking send status", queued: "Queued in Smartlead", sent: "Sent via Smartlead", failed: "Sending needs attention", uncertain: "Checking send status", suppressed: "Outreach stopped" }[status];
}
